<!-- This whole component will be replaced with new image navigation -->
<template>
  <div class="flex-none hidden lg:block">
    <!-- Start Bottom Desktop -->
    <div class="w-auto h-auto !bg-opacity-90">
      <div class="relative flex justify-center items-start">
        <!-- Start Bottom Left -->
        <div class="flex gap-2 relative items-start">
          <!-- Reduce max-width by the width of the UI elements bottom right to avoid collision -->

          <UploadButton @click="uploadFile" class="upload-btn sm:h-16 sm:w-16" />

          <div
            class="overflow-x-scroll overflow-auto scroll-container"
            style="max-width: calc(100vw - 150px); overflow-y: visible"
          >
            <div class="flex gap-2 items-start">
              <div v-for="image in store.images" :key="image.meta.id">
                <ImageButton
                  v-if="
                    image.previewResult.state === ProcessingState.Error || image.original.state === UploadState.Error
                  "
                  :selected="store.selectedImage === image"
                  class="relative"
                  @click="deleteImage(image)"
                >
                  <template #action>
                    <WarningIcon />
                  </template>
                  <HTMLImage
                    :src="image.meta.preview"
                    img-classes="object-cover !w-12 !h-12 sm:!w-16 sm:!h-16 bg-white checkerboard"
                  />
                </ImageButton>

                <ImageButton
                  v-else-if="image.previewResult.state === ProcessingState.Processing"
                  :selected="store.selectedImage === image"
                  class="relative"
                  loading
                >
                  <HTMLImage
                    :src="image.meta.preview"
                    img-classes="object-cover !w-12 !h-12 sm:!w-16 sm:!h-16 bg-white checkerboard"
                  />
                </ImageButton>
                <Dropdown
                  v-else-if="store.selectedImage === image"
                  align="middle"
                  direction="top"
                  vertical-offset="8"
                  teleport-destination="#page"
                  class="flex"
                >
                  <template #trigger>
                    <ImageButton selected as="span">
                      <HTMLImage
                        :src="previewUrl(image)"
                        img-classes="object-cover !w-12 !h-12 sm:!w-16 sm:!h-16 bg-white checkerboard"
                      />
                      <template #action>
                        <ChevronUpIcon />
                      </template>
                    </ImageButton>
                  </template>
                  <template #content>
                    <ImageNavigationContextMenu
                      :delete-image="handleDeleteImage"
                      :requires-delete-confirmation="requiresDeleteConfirmation"
                      :selected-image="store.selectedImage"
                    />
                  </template>
                </Dropdown>
                <ImageButton v-else @click="store.selectImage(image)">
                  <HTMLImage
                    :src="previewUrl(image)"
                    img-classes="object-cover !w-12 !h-12 sm:!w-16 sm:!h-16 bg-white checkerboard"
                  />
                </ImageButton>
              </div>

              <!-- Empty element to have some more space for horizontal scrolling -->
              <div class="block shrink-0 !w-12 !h-12 sm:!w-16 sm:!h-16 pointer-events-none"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- End Bottom Left -->
  </div>
</template>

<script setup lang="ts">
import { Image as HTMLImage, ImageButton, WarningIcon, UploadButton, ChevronUpIcon, Dropdown } from "prism";
import { Image, UploadState } from "@/modules/internal_api/image";
import { ProcessingState } from "@/modules/internal_api/image";
import { rbgRemoveImageV100 } from "kaleido-event-tracker";
import originalPlaceholder from "@assets/images/inline-editor/original_placeholder.jpg";

import ImageNavigationContextMenu from "./image_navigation_context_menu.vue";

import Client from "@/modules/internal_api/client";

import { useEditorStore } from "@/stores/editor_store";
import { useDeleteConfirmation } from "@/composables/delete_confirmation";

const store = useEditorStore();
const { requiresDeleteConfirmation } = useDeleteConfirmation();

const uploadFile = () => {
  window.onUploadFile();
};

const previewUrl = (image: Image) => {
  if (
    image.meta.preview === originalPlaceholder &&
    image.original.state === UploadState.Finished &&
    "url" in image.original
  ) {
    return image.original.url;
  }
  return image.meta.preview;
};

const deleteImage = (image: Image) => {
  Client.deleteImage(image);
  store.deleteImage(image);
  window.track("Images", "remove_result", "Remove result");
  rbgRemoveImageV100({ image_id: image.meta.id });
};

const handleDeleteImage = () => {
  if (store.selectedImage) {
    deleteImage(store.selectedImage);
  }
};
</script>

<style scoped>
.scroll-container::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 1rem;
}

.scroll-container::-webkit-scrollbar-thumb {
  border: 4px solid rgba(0, 0, 0, 0);
  background-clip: padding-box;
  -webkit-border-radius: 7px;
  background-color: rgba(0, 0, 0, 0.15);
  -webkit-box-shadow: inset -1px -1px 0px rgba(0, 0, 0, 0.05), inset 1px 1px 0px rgba(0, 0, 0, 0.05);
}
</style>
